import { precision } from "@geobuf/precision";
import { api, toApiError } from "@services/api";
import type { YieldGates } from "@screens/TracksScreen/DeviceWindow/panelYield";

const BASE_URL = process.env.REACT_APP_API_URL ?? window.location.origin;

// DeviceType enum — matches proto values used by TracksScreen
export enum DeviceType {
  DEVICE_TYPE_UNSPECIFIED = 0,
  DEVICE_TYPE_RETROFIT = 1,
  DEVICE_TYPE_CAN = 2,
  DEVICE_TYPE_POTATO = 3,
}

// DeviceSetting wraps settings and exposes proto-style getters used by TracksScreen
export class DeviceSetting {
  private _width: number;
  private _delay: number;
  private _type: DeviceType;
  private _sensorMin: number;
  private _sensorMax: number;
  private _distance: number;
  private _sensorMode: number;
  private _minSatellitesCount: number;
  private _isStaticDevice: boolean;
  private _revision: number;
  private _minTurnRadiusM: number | null;

  constructor(data: any) {
    this._width = data.width ?? 6;
    this._delay = data.delay ?? 16000;
    this._type = data.deviceType ?? DeviceType.DEVICE_TYPE_RETROFIT;
    this._sensorMin = data.sensorMin ?? 0;
    this._sensorMax = data.sensorMax ?? 0;
    this._distance = data.distance ?? 0;
    this._sensorMode = data.sensorMode ?? 0;
    this._minSatellitesCount = data.minSatellitesCount ?? 0;
    this._isStaticDevice = data.isStaticDevice ?? false;
    this._revision = data.revision ?? 0;
    this._minTurnRadiusM = typeof data.minTurnRadiusM === "number" ? data.minTurnRadiusM : null;
  }

  getWidth(): number { return this._width; }
  getDelay(): number { return this._delay; }
  getType(): DeviceType { return this._type; }
  getSensorMin(): number { return this._sensorMin; }
  getSensorMax(): number { return this._sensorMax; }
  getDistance(): number { return this._distance; }
  getSensorMode(): number { return this._sensorMode; }
  getMinSatellitesCount(): number { return this._minSatellitesCount; }
  getIsStaticDevice(): boolean { return this._isStaticDevice; }
  getRevision(): number { return this._revision; }
  /** Analytics turn gate, metres (0 = off); null when the backend sends none. */
  getMinTurnRadiusM(): number | null { return this._minTurnRadiusM; }
}

// Timestamp-like wrapper so getLastSeenAt().toDate() works
class TimestampLike {
  private _date: Date;
  constructor(isoString: string) {
    this._date = new Date(isoString);
  }
  toDate(): Date { return this._date; }
}

// Farm-scoped device visibility: allFarms = visible under every farm;
// otherwise visible only under farmIds. Owner-editable in the device dialog.
export type TDeviceFarms = {
  allFarms: boolean;
  farmIds: string[];
};

// AttachedDevice wraps a device JSON and exposes proto-style getters
export class AttachedDevice {
  private _id: string;
  private _esn: string;
  private _name: string;
  private _description: string;
  private _lastSeenAt: TimestampLike | undefined;
  private _lastPosition: number[];
  private _settings: DeviceSetting | undefined;
  private _farms: TDeviceFarms;
  private _source: TDeviceSource;
  private _johnDeere: TJohnDeereImportStatus | null;

  constructor(data: any) {
    this._source = data.source === "johndeere" ? "johndeere" : "greengrowth";
    this._johnDeere =
      this._source === "johndeere" && data.johnDeere
        ? {
            lastImportAt: data.johnDeere.lastImportAt ?? null,
            errorCode: data.johnDeere.errorCode ?? null,
            error: data.johnDeere.error ?? null,
          }
        : null;
    this._id = data.id ?? "";
    this._esn = data.esn ?? "";
    this._name = data.name ?? "";
    this._description = data.description ?? "";
    this._lastSeenAt = data.lastSeenAt ? new TimestampLike(data.lastSeenAt) : undefined;
    this._lastPosition = data.lastPosition ?? [];
    if (data.settings) {
      this._settings = new DeviceSetting(data.settings);
    }
    this._farms = {
      allFarms: data.farms?.allFarms ?? true,
      farmIds: data.farms?.farmIds ?? [],
    };
  }

  getId(): string { return this._id; }
  getEsn(): string { return this._esn; }
  getName(): string { return this._name; }
  getDescription(): string { return this._description; }
  getLastSeenAt(): TimestampLike | undefined { return this._lastSeenAt; }
  getLastPositionList(): number[] { return this._lastPosition; }
  getSettings(): DeviceSetting | undefined { return this._settings; }
  getReaperWidth(): number { return this._settings?.getWidth() ?? 6; }
  getFarms(): TDeviceFarms { return this._farms; }
  getSource(): TDeviceSource { return this._source; }
  getJohnDeere(): TJohnDeereImportStatus | null { return this._johnDeere; }
}

// Fewest above-zero moisture readings that count as "this machine has the
// sensor". Kept in step with MIN_MOISTURE_POINTS in
// backend-ai/src/services/fieldTracksService.ts.
const MIN_MOISTURE_POINTS = 20;

// DeviceStatisticsResponse wraps track data and exposes proto-style getters.
// Backend computes normalized values (norm) from device settings; frontend just consumes.
export class DeviceStatisticsResponse {
  private _length: number;
  private _time: number[];
  private _coords: number[];
  private _norm: number[];
  private _speed: number[];
  private _satQty: number[];
  private _sensor: number[];
  private _frequency: number[];
  private _weight: number[];
  // Potato-camera per-point data. null distinguishes "no camera" from "camera
  // saw nothing" (0), so these arrays stay nullable — never coerced to 0.
  private _potatoCount: (number | null)[];
  private _stoneCount: (number | null)[];
  private _sizeHist: (number[] | null)[];
  private _hasCamera: boolean;
  // Grain-moisture readings (%). Only some machines carry the sensor; the rest
  // report a flat 0, which is why zero is stored as null (nothing measured)
  // rather than as a real reading.
  private _moisture: (number | null)[];
  private _moistureCount: number;
  // Fuel sensor input in mV (null = no reading). Present only for devices an
  // admin marked "fuel sensor connected"; _fuelSensor is that switch.
  private _fuelMv: (number | null)[];
  private _fuelCount: number;
  private _fuelSensor: boolean;
  // Fewest satellites a fix must report to be drawn, from the device's own
  // `min_satellites_count` setting. 0 (the default) keeps every point that has
  // a position: some GPS modules report a valid location with a satellite
  // count of 0, and a hard-coded threshold blanked their tracks entirely.
  private _minSatellites: number;
  // Yield inputs, present only when requested with `yield: true` (see
  // TracksScreen/DeviceWindow/panelYield.ts). null per point = not harvesting.
  private _yieldAmount: (number | null)[];
  private _coeff: (number | null)[];
  private _widthM: (number | null)[];
  private _yieldGates: YieldGates | null;

  constructor(data: any) {
    const tracks: any[] = data.tracks ?? [];
    this._length = tracks.length;
    this._minSatellites = data.minSatellitesCount ?? 0;

    const { encode } = precision(10);

    this._time = new Array(tracks.length);
    this._coords = new Array(tracks.length * 2);
    this._norm = new Array(tracks.length);
    this._speed = new Array(tracks.length);
    this._satQty = new Array(tracks.length);
    this._sensor = new Array(tracks.length);
    this._frequency = new Array(tracks.length);
    this._weight = new Array(tracks.length);
    this._potatoCount = new Array(tracks.length);
    this._stoneCount = new Array(tracks.length);
    this._sizeHist = new Array(tracks.length);
    this._hasCamera = false;
    this._moisture = new Array(tracks.length);
    this._moistureCount = 0;
    this._fuelMv = new Array(tracks.length);
    this._fuelCount = 0;
    this._fuelSensor = data.fuelSensor === true;
    this._yieldAmount = new Array(tracks.length);
    this._coeff = new Array(tracks.length);
    this._widthM = new Array(tracks.length);
    this._yieldGates = data.yieldGates ?? null;

    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i];
      this._time[i] = Math.floor(new Date(t.time).getTime() / 1000);
      this._coords[i * 2] = encode(t.longitude ?? 0);
      this._coords[i * 2 + 1] = encode(t.latitude ?? 0);
      this._norm[i] = t.norm != null ? t.norm : 0;
      this._speed[i] = t.speed ?? 0;
      this._satQty[i] = t.satQty ?? 0;
      this._sensor[i] = t.value ?? 0;
      this._frequency[i] = t.freq ?? 0;
      this._weight[i] = t.weight ?? 0;
      this._yieldAmount[i] = t.yieldAmount ?? null;
      this._coeff[i] = t.coeff ?? null;
      this._widthM[i] = t.widthM ?? null;
      this._potatoCount[i] = t.cvPotatoCount ?? null;
      this._stoneCount[i] = t.cvStoneCount ?? null;
      this._sizeHist[i] = Array.isArray(t.cvSizeHist) ? t.cvSizeHist : null;
      if (this._potatoCount[i] != null) this._hasCamera = true;
      // 0 means "no sensor connected", not "bone dry" — the firmware sends the
      // field either way. Anything at or below zero (or above 100) is dropped so
      // the moisture layer draws nothing over ground it never measured.
      const m = t.moisturePercent;
      if (m != null && m > 0 && m <= 100) {
        this._moisture[i] = m;
        this._moistureCount++;
      } else {
        this._moisture[i] = null;
      }
      const fuel = t.fuelMv;
      if (fuel != null && Number.isFinite(fuel)) {
        this._fuelMv[i] = fuel;
        this._fuelCount++;
      } else {
        this._fuelMv[i] = null;
      }
    }
  }

  getLength(): number { return this._length; }
  getTimeList(): number[] { return this._time; }
  getCoordsList(): number[] { return this._coords; }
  getNormList(): number[] { return this._norm; }
  getSpeedList(): number[] { return this._speed; }
  getSatQtyList(): number[] { return this._satQty; }
  /** Per-device satellite floor for drawing a fix (0 = no satellite filter). */
  getMinSatellites(): number { return this._minSatellites; }
  getSensorList(): number[] { return this._sensor; }
  getFrequencyList(): number[] { return this._frequency; }
  /** Raw weight per point (before calibration). Backend computes: potato=value+freq, retrofit/CAN=value/freq */
  getWeightList(): number[] { return this._weight; }
  /** Potato-camera counts per point (null = no camera at that point). */
  getPotatoCountList(): (number | null)[] { return this._potatoCount; }
  getStoneCountList(): (number | null)[] { return this._stoneCount; }
  /** 8-bucket size histogram per point (null = no camera at that point). */
  getSizeHistList(): (number[] | null)[] { return this._sizeHist; }
  /** True when at least one point carries potato-camera data. */
  hasCameraData(): boolean { return this._hasCamera; }
  /** Grain moisture per point in % (null = nothing measured at that point). */
  getMoistureList(): (number | null)[] { return this._moisture; }
  /** True when the machine really measured moisture over this window.
   *
   *  Mirrors the MIN_MOISTURE_POINTS gate the backend applies to the field
   *  response: a few scattered non-zero fixes are noise, not a sensor. */
  hasMoistureData(): boolean { return this._moistureCount >= MIN_MOISTURE_POINTS; }
  /** Fuel sensor input per point in mV (null = no reading at that point). */
  getFuelMvList(): (number | null)[] { return this._fuelMv; }
  /** True when the device has a fuel sensor and this window carries readings. */
  hasFuelData(): boolean { return this._fuelSensor && this._fuelCount > 0; }
  /** Per point: mass signal under the server's sensor rule (null = not harvesting). */
  getYieldAmountList(): (number | null)[] { return this._yieldAmount; }
  /** Per point: calibration coefficient in force. */
  getCoeffList(): (number | null)[] { return this._coeff; }
  /** Per point: header width in force, metres. */
  getWidthList(): (number | null)[] { return this._widthM; }
  /** Speed band / static flag for the yield panel; null when not requested. */
  getYieldGates(): YieldGates | null { return this._yieldGates; }
}

/**
 * One machine's points over [from, to] as CSV, KML or Shapefile (zip) — the
 * same points and columns as Fields → tracks → Export points: delay-shifted,
 * with yield, mass and sensor delta in the user's units. The filters are the
 * Tracks screen's, so the file matches the map.
 */
export const deviceTracksExportUrl = (
  id: string,
  from: Date,
  to: Date,
  format: "csv" | "kml" | "shp",
  opts: {
    units: { weight: string; area: string; poundsPerBushel: number; poundsPerBag: number };
    hiddenRanges?: ReadonlyArray<{ lo: number; hi: number }>;
    speedMinKmh?: number | null;
    speedMaxKmh?: number | null;
    removeTurns?: boolean;
    /** Typed into the Tracks panel: replaces the calibration for every point. */
    coefficient?: number | null;
  }
): string => {
  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
    format,
    weight: opts.units.weight,
    area: opts.units.area,
    poundsPerBushel: String(opts.units.poundsPerBushel),
    poundsPerBag: String(opts.units.poundsPerBag),
  });
  if (opts.hiddenRanges?.length) {
    params.append("hiddenRanges", opts.hiddenRanges.map((r) => `${r.lo}-${r.hi}`).join(","));
  }
  if (opts.speedMinKmh != null) params.append("speedMin", String(opts.speedMinKmh));
  if (opts.speedMaxKmh != null) params.append("speedMax", String(opts.speedMaxKmh));
  if (opts.removeTurns) params.append("removeTurns", "1");
  if (opts.coefficient != null && opts.coefficient > 0) params.append("coefficient", String(opts.coefficient));
  return `${BASE_URL}/api/v1/devices/${id}/tracks/export?${params}`;
};

type TDeviceHarvester = {
  name: string;
  model: string;
  default_reaper: {
    width: number;
  };
};

export type TCreateDeviceParams = {
  serial_number: string;
  harvester: TDeviceHarvester;
};

export type TEditDeviceParams = {
  harvester: TDeviceHarvester;
  /** Milliseconds. Omitted = leave the stored delay untouched. */
  delay?: number;
  /**
   * false = the width field was not changed: no width is sent, so renaming a
   * machine never writes the width it was showing into the timeline as a hand
   * edit (an imported John Deere combine showed 6.7 m before its first import
   * and froze there). Omitted = send it.
   */
  sendWidth?: boolean;
};

type TTagColor = {
  text: string;
  background: string;
};

export type TTag = {
  id: string;
  text: string;
  color: TTagColor;
};

export type TDeviceStatus = "inactive" | "active" | "archived";

/**
 * Where a device's data comes from: "greengrowth" = our IoT hardware;
 * "johndeere" = a combine imported from John Deere Operations Center, whose
 * harvest data arrives in batches (no live connection, no sensor health).
 */
export type TDeviceSource = "greengrowth" | "johndeere";

/**
 * Harvest import status of a John Deere combine (GET /devices `johnDeere`).
 * `errorCode` is one of the backend's JD_ERROR_CODES (FIELD_OPERATIONS_DENIED,
 * EXPORT_REFUSED, DEERE_THROTTLED, NOT_CONNECTED, UNSUPPORTED_EXPORT,
 * IMPORT_FAILED) and stays until the failing step succeeds; `error` is its
 * English detail.
 */
export type TJohnDeereImportStatus = {
  lastImportAt: string | null;
  errorCode: string | null;
  error: string | null;
};

export type TDevice = {
  // Attachment id
  id: string;
  // Electronic serial number
  esn: string;
  // User provided information
  harvester: TDeviceHarvester;
  // Array of tags
  tags: Array<TTag>;
  // Device status
  status: TDeviceStatus;
  // Device type
  deviceType?: DeviceType;
  // Static device flag (potato only)
  isStaticDevice?: boolean;
  // Which farms the device is visible under (owner-editable)
  farms?: TDeviceFarms;
  // ISO timestamp of the last device connection (null/undefined = never seen)
  lastSeenAt?: string | null;
  // Grain-flow delay in milliseconds (devices.delay) — the same column the
  // admin dialog edits. Undefined when the source did not carry it.
  delay?: number;
  // Data source; undefined = greengrowth (older payloads).
  source?: TDeviceSource;
  // John Deere combines only: last import and why it is failing.
  johnDeere?: TJohnDeereImportStatus | null;
};

type TDevicesListResponse = {
  devices: Array<TDevice>;
};

// ── Device health chart ─────────────────────────────────────────────────────

/** Severity of a day's verdict; drives the sparkline's shaded bands. */
export type THealthSeverity = "critical" | "advisory" | "ok";

export type THealthDay = {
  /** UTC calendar day, "YYYY-MM-DD". */
  day: string;
  /** ok | insufficient | unavailable | stationary | saturated | stuck | silent | dead | flat | intermittent | no_fix | degraded */
  status: string;
  severity: THealthSeverity;
  /**
   * Mean |value| / |freq| over the day's working points; null when the day is
   * empty. Which one the chart plots is `TDeviceHealth.channel`, not a guess.
   */
  avgValue: number | null;
  avgFreq: number | null;
  nWork: number;
};

export type TDeviceHealth = {
  esn: string;
  kind: "potato" | "grain" | null;
  /**
   * Which mean the trend line should plot. A FREQUENCY-mode potato device
   * (sensor_mode 2) makes its yield from `freq`, and its `value` channel is not
   * what the verdict judges — so the backend resolves the choice and sends it
   * rather than leaving the client to re-derive it from the sensor mode.
   */
  channel: "value" | "freq";
  days: Array<THealthDay>;
};

/**
 * Health cells for every device the user can see, keyed by attachment id.
 *
 * One request for the whole list rather than one per row — the backend reads a
 * cached daily table, so the batch costs the same as a single device would.
 */
/** Sparse "YYYY-MM-DD" -> working seconds, per attachment id. */
export type TDeviceActivity = Record<string, number>;

export type TActivityResponse = {
  days: number;
  devices: Record<string, TDeviceActivity>;
};

/**
 * A year of harvest activity for every visible device, for the row grids.
 *
 * Sparse on the wire — only days with recorded work — so a year across a small
 * fleet is a few hundred numbers rather than thousands of empty cells.
 */
export const getDevicesActivity = async (): Promise<TActivityResponse> =>
  api.get<TActivityResponse>("/api/v1/devices/activity", undefined, { silent: true });

export const getDevicesHealth = async (): Promise<Record<string, TDeviceHealth>> =>
  api.get<Record<string, TDeviceHealth>>(
    "/api/v1/devices/health",
    // Day count is left to the backend, which defaults to the window its
    // rollup actually maintains.
    undefined,
    // The chart is decoration on a list that renders fine without it; a failure
    // here must not pop a toast over the devices the user actually came for.
    { silent: true }
  );

/**
 * GET /api/v1/devices/:id — mirrors backend-ai deviceService.getDeviceDetails.
 * Settings are FLAT on this response (deviceType, isStaticDevice, delay …),
 * not nested under `settings` like the list endpoint's rows.
 */
type TDeviceDetailDto = {
  id: string;
  esn: string;
  name: string | null;
  description: string | null;
  model: string | null;
  deviceType: DeviceType | null;
  sensorMode: number | null;
  sensorMin: number | null;
  sensorMax: number | null;
  width: number | null;
  distance: number | null;
  delay: number | null;
  minSatellitesCount: number | null;
  isStaticDevice: boolean | null;
  lastSeenAt: string | null;
  lastPosition: [number, number] | null;
  farms?: { allFarms?: boolean; farmIds?: string[] };
};

export const getDeviceById = async (
  id: string
): Promise<TDevice | undefined> => {
  const url = `${BASE_URL}/api/v1/devices/${id}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch device");
  }
  const device: TDeviceDetailDto = await response.json();
  return {
    id: device.id,
    esn: device.esn,
    tags: [],
    status: "active",
    harvester: {
      name: device.name ?? "",
      model: device.description ?? "",
      default_reaper: { width: device.width ?? 6 },
    },
    deviceType: device.deviceType ?? DeviceType.DEVICE_TYPE_RETROFIT,
    isStaticDevice: device.isStaticDevice ?? false,
    delay: device.delay ?? undefined,
    farms: {
      allFarms: device.farms?.allFarms ?? true,
      farmIds: device.farms?.farmIds ?? [],
    },
  };
};

export const getDevicesData = async (): Promise<TDevicesListResponse> => {
  const response = await userDevices();
  return {
    devices: response.devices.map((device) => ({
      id: device.getId(),
      esn: device.getEsn(),
      tags: [],
      status: "active" as TDeviceStatus,
      harvester: {
        name: device.getName(),
        model: device.getDescription(),
        default_reaper: { width: device.getReaperWidth() },
      },
      farms: device.getFarms(),
      lastSeenAt: device.getLastSeenAt()?.toDate().toISOString() ?? null,
      // Carried through so the list can branch on machine type — the Technical
      // status button is potato-only. The list is built from these getters; the
      // single-device fetch (getDeviceById) reads the same fields flat.
      deviceType: device.getSettings()?.getType(),
      isStaticDevice: device.getSettings()?.getIsStaticDevice(),
      delay: device.getSettings()?.getDelay(),
      source: device.getSource(),
      johnDeere: device.getJohnDeere(),
    })),
  };
};

export const createDevice = async (params: TCreateDeviceParams) => {
  const url = `${BASE_URL}/api/v1/devices`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      serial_number: params.serial_number,
      harvester: params.harvester,
    }),
    mode: "cors",
  });
  if (!response.ok) {
    throw await toApiError(response, "Failed to create device");
  }
};

export const editDevice = async (id: string, params: TEditDeviceParams) => {
  const url = `${BASE_URL}/api/v1/devices/${id}`;
  const response = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: params.harvester.name,
      description: params.harvester.model,
      ...(params.sendWidth !== false ? { width: params.harvester.default_reaper.width } : {}),
      ...(params.delay !== undefined ? { delay: params.delay } : {}),
    }),
    mode: "cors",
  });
  if (!response.ok) {
    throw await toApiError(response, "Failed to edit device");
  }
};

// Owner-only: set which farms the device is visible under.
export const setDeviceFarms = async (id: string, farms: TDeviceFarms) => {
  const url = `${BASE_URL}/api/v1/devices/${id}/farms`;
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      allFarms: farms.allFarms,
      farmIds: farms.allFarms ? [] : farms.farmIds,
    }),
    mode: "cors",
  });
  if (!response.ok) {
    throw await toApiError(response, "Failed to update device farms");
  }
};

/**
 * One line of a device's header-width history.
 *
 * Width is a timeline, not a single value: the machine's header can change
 * mid-season, and analytics attribute each harvested day to the width that was
 * actually fitted then. `effectiveFrom` on the earliest segment governs every
 * timestamp before the next one starts.
 *
 * Always metres, like every other width in the API — the UI converts for
 * display only.
 */
export type TWidthSegment = {
  id: string;
  effectiveFrom: string;
  widthMeters: number;
  note: string | null;
  createdAt: string;
};

export const getDeviceWidthSegments = async (
  id: string
): Promise<TWidthSegment[]> => {
  const url = `${BASE_URL}/api/v1/devices/${id}/width-segments`;
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) {
    throw await toApiError(response, "Failed to load header width history");
  }
  const body = await response.json();
  return body.segments ?? [];
};

/**
 * Replaces the WHOLE list — there is no per-row edit, because validity is a
 * property of the set (at least one segment, chronological, no two starting at
 * the same instant, none in the future).
 */
export const setDeviceWidthSegments = async (
  id: string,
  segments: Array<{ effectiveFrom: string; widthMeters: number; note?: string | null }>
): Promise<TWidthSegment[]> => {
  const url = `${BASE_URL}/api/v1/devices/${id}/width-segments`;
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ segments }),
    mode: "cors",
  });
  if (!response.ok) {
    throw await toApiError(response, "Failed to update header width history");
  }
  const body = await response.json();
  return body.segments ?? [];
};

export type BBox = [number, number, number, number];

type Devices = {
  devices: AttachedDevice[],
  bbox: BBox | null,
};

export const userDevices = async (): Promise<Devices> => {
  const url = `${BASE_URL}/api/v1/devices`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch devices");
  }

  const body = await response.json();
  const rawDevices: any[] = body.devices ?? [];

  const devices = rawDevices.map((d: any) => new AttachedDevice(d));

  // A bbox corner at 0,0 means some device reported a no-fix position; fitting
  // to it zooms the map out to the whole world, so drop the bbox entirely and
  // let the map keep its default view.
  let bbox: BBox | null = null;
  if (body.bbox && body.bbox.length === 4) {
    const candidate = body.bbox as BBox;
    const nullIsland =
      candidate.some((v: number) => !Number.isFinite(v)) ||
      (Math.abs(candidate[0]) < 0.001 && Math.abs(candidate[1]) < 0.001) ||
      (Math.abs(candidate[2]) < 0.001 && Math.abs(candidate[3]) < 0.001);
    if (!nullIsland) {
      bbox = candidate;
    }
  }

  return { devices, bbox };
};

export const deviceTrackDates = async (
  id: string,
  from: Date,
  to: Date
): Promise<Date[]> => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
    tz,
  });
  const url = `${BASE_URL}/api/v1/devices/${id}/tracks/dates?${params}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch track dates");
  }

  const body = await response.json();
  // Parse "YYYY-MM-DD" as a LOCAL date so the calendar dot lands on the
  // correct day regardless of the user's UTC offset.
  return (body.dates ?? []).map((d: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return new Date(d);
  });
};

export const deviceStatistics = async (
  id: string,
  from: Date,
  to: Date,
  opts: { yield?: boolean } = {}
): Promise<DeviceStatisticsResponse> => {
  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  });
  // The Tracks yield panel's inputs (server-side gates, coefficients, widths).
  if (opts.yield) params.set("yield", "1");
  const url = `${BASE_URL}/api/v1/devices/${id}/tracks?${params}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch device tracks");
  }

  const body = await response.json();
  return new DeviceStatisticsResponse(body);
};

/** One time bucket from `/api/v1/devices/:id/tracks/metrics`. */
export type TTrackMetricPoint = {
  /** Bucket START, ISO. Buckets are anchored to the requested `from`. */
  time: string;
  /** Raw rows behind this bucket. */
  n: number;
  /**
   * Mean over the bucket, with the extremes alongside. Plot the mean as the
   * line and min/max as a band: these traces are mostly narrow spikes, and a
   * mean-only series at 87 s buckets hides exactly what the page is for.
   * Null when no row in the bucket carried the reading.
   */
  value: number | null;
  valueMin: number | null;
  valueMax: number | null;
  freq: number | null;
  freqMin: number | null;
  freqMax: number | null;
  speed: number | null;
  /**
   * Visible GPS satellites. `satellitesMin` is the diagnostic one — a bucket
   * whose floor is 0 lost the fix somewhere inside it, and the mean over 87 s
   * would smooth that away. Null when the device reports no count at all.
   */
  satellites: number | null;
  satellitesMin: number | null;
  satellitesMax: number | null;
  /** Position of the bucket. Null when it had no usable fix. */
  latitude: number | null;
  longitude: number | null;
};

export type TTrackMetricsResponse = {
  device: {
    esn: string;
    name: string;
    deviceType: DeviceType;
    sensorMin: number;
    sensorMax: number;
    sensorMode: number;
    minSatellitesCount: number;
    width: number;
    delay: number;
  };
  from: string;
  to: string;
  /** Width of one bucket. Shown to the user so the resolution is never implied. */
  bucketSeconds: number;
  length: number;
  points: TTrackMetricPoint[];
};

export type TTrackDaysResponse = {
  /** Calendar days ("YYYY-MM-DD") in the CALLER'S timezone. */
  days: string[];
  /** ISO bounds of what can actually be plotted, or null if the device has none. */
  earliest: string | null;
  latest: string | null;
};

/**
 * Which days to dot on the Technical status calendar.
 *
 * Cheap by construction: the server walks the time index one day at a time
 * (a loose index scan) instead of the `GROUP BY DATE(time)` over raw points that
 * the Tracks screen calendar runs — 1.5 ms versus 17.5 s for one month on a busy
 * potato device, with identical output. A calendar refetches on every month
 * change, so that difference is the whole reason this endpoint exists.
 *
 * `earliest` matters: it bounds what can actually be plotted, so the calendar
 * does not offer days that come back empty.
 */
export const deviceTrackDays = async (
  id: string,
  from: string,
  to: string
): Promise<TTrackDaysResponse> => {
  // Days are cut at LOCAL midnight, matching the Tracks and Analytics calendars.
  // Same tz contract as deviceTrackDates.
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const params = new URLSearchParams({ from, to, tz });
  const url = `${BASE_URL}/api/v1/devices/${id}/tracks/days?${params}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw await toApiError(response, "Failed to fetch track days");
  }
  return response.json();
};

/**
 * Bucketed telemetry for the Technical status page.
 *
 * Server-side downsampling, unlike `deviceStatistics` above, which returns raw
 * points and silently truncates at 50k — about 14 h for a potato device. Here
 * the response size is set by `points`, not by the window, so zooming out is
 * cheap and zooming in genuinely adds resolution (the bucket shrinks).
 *
 * Potato devices only; anything else 404s. `from` must be within the last 48 h.
 *
 * `points` defaults to the server maximum on purpose: the page exists to show
 * what the sensor actually did, so the bucket has to be as narrow as the API
 * allows. At 5000 buckets a 24 h window is ~17 s and anything under ~83 min
 * hits the 1 s floor, i.e. one raw row per bucket and no averaging at all.
 */
export const deviceTrackMetrics = async (
  id: string,
  from: Date,
  to: Date,
  points = 5000
): Promise<TTrackMetricsResponse> => {
  const params = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
    points: String(points),
  });
  const url = `${BASE_URL}/api/v1/devices/${id}/tracks/metrics?${params}`;
  const response = await fetch(url);
  if (!response.ok) {
    // statusCode is set: the Technical screen reads 404 as "potato only".
    throw await toApiError(response, "Failed to fetch device metrics");
  }
  return response.json();
};

/**
 * Same payload the Tracks map expects, built from admin `/api/admin/tracks` rows
 * (server includes `norm` using the same rules as the client statistics API).
 */
export function deviceStatisticsFromAdminTracks(
  rawTracks: Array<{
    time: string;
    longitude: number;
    latitude: number;
    value: number | null;
    freq: number | null;
    speed: number;
    satQty?: number | null;
    norm?: number | null;
    cvPotatoCount?: number | null;
    cvStoneCount?: number | null;
    cvSizeHist?: number[] | null;
    moisturePercent?: number | null;
  }>,
): DeviceStatisticsResponse {
  return new DeviceStatisticsResponse({
    tracks: rawTracks.map((t) => ({
      time: t.time,
      longitude: t.longitude,
      latitude: t.latitude,
      value: t.value,
      freq: t.freq,
      norm: t.norm ?? 0,
      speed: t.speed,
      satQty: t.satQty ?? 99,
      weight: 0,
      cvPotatoCount: t.cvPotatoCount ?? null,
      cvStoneCount: t.cvStoneCount ?? null,
      cvSizeHist: t.cvSizeHist ?? null,
      moisturePercent: t.moisturePercent ?? null,
    })),
  });
}
